import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../api/ApiProvider";
import axios from "axios";
import { getRegisteredAuthStore } from "../../api/axiosConfig";
import {
  CreateRechnungDto,
  CreatePatientTransactionDto,
  CreateServicesTransactionDto,
  CreateBankTransferDto,
  DebitPermissionDto,
  UpdateExportStatusDto,
  BaseTransactionDetailsDto,
  CreateRefundDto,
  SendBillEmailDto,
  GetRechnungDetailsDto,
  WaivableFee,
} from "../../api-client";
import { transactionKeys } from "./transaction.keys";
import { taxationKeys } from "../Taxation/taxation.keys";

/**
 * NOTE: Global error/success handling is configured in src/stores/queryClient.ts
 * - MutationCache handles automatic error notifications via notificationStore
 * - MutationCache handles automatic success notifications via meta.successMessage
 * - No need to manually call notificationStore in individual mutations
 */

// ===========================
// PATIENT BILLING MUTATIONS
// ===========================

/**
 * Replaces: createRechnung from TransactionsStore
 * Used by: PatientBillTable "Rechnung erstellen" button
 */
export const useCreatePatientRechnung = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateRechnungDto) => transaction.createRechnung(dto),
    onSuccess: (_data, dto) => {
      const pid = dto.patientId;
      const rxId = dto.rxId;
      // Return the invalidation promises so the mutation stays `pending` until the
      // refetched data has landed. This keeps the global "Bitte warten" backdrop up
      // (and the "Rechnung erstellen" button disabled) until the UI reflects the new
      // rechnung, preventing a double-click race that would create duplicate bills.
      const invalidations: Promise<void>[] = [];
      if (pid) {
        invalidations.push(
          qc.invalidateQueries({
            queryKey: transactionKeys.patientRechnungen.forPatient(pid),
          }),
          qc.invalidateQueries({
            queryKey: transactionKeys.patientBillsInfo.forPatient(pid),
          }),
          qc.invalidateQueries({
            queryKey: transactionKeys.unpaidBadgeCount.forPatient(pid),
          }),
        );
      }
      if (rxId) {
        invalidations.push(
          qc.invalidateQueries({
            queryKey: transactionKeys.rxRechnungen.forRx(rxId),
          }),
          qc.invalidateQueries({
            queryKey: transactionKeys.rxDuePayment.forRx(rxId),
          }),
          qc.invalidateQueries({
            queryKey: transactionKeys.rxBillingTimeline.forRx(rxId),
          }),
        );
      }
      return Promise.all(invalidations);
    },
  });
};

/**
 * Sets which automatic fees of an Rx are waived (e.g. the PBE of a private Rx).
 * The fee drops out of the Rx price, so every bill figure of the Rx is refetched.
 * A 409 (live Rechnung) reaches the user through the global MutationCache handler.
 * Used by: PatientBillDetailsModalShad (RxFeeWaivers)
 */
export const useSetRxFeeWaivers = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ rxId, waivedFees }: { rxId: string; waivedFees: WaivableFee[] }) =>
      transaction.setRxFeeWaivers(rxId, { waivedFees }),
    onSuccess: (data, { rxId }) => {
      qc.setQueryData(transactionKeys.rxFeeWaivers.forRx(rxId), data);
      return Promise.all([
        qc.invalidateQueries({ queryKey: transactionKeys.patientBillsInfo.all() }),
        qc.invalidateQueries({ queryKey: transactionKeys.unpaidBadgeCount.all() }),
        qc.invalidateQueries({ queryKey: transactionKeys.rxDuePayment.forRx(rxId) }),
        qc.invalidateQueries({ queryKey: transactionKeys.duePaymentByRx.forRx(rxId) }),
      ]);
    },
    onError: (_error, { rxId }) => {
      // the server's view (e.g. a Rechnung created meanwhile) wins
      qc.invalidateQueries({ queryKey: transactionKeys.rxFeeWaivers.forRx(rxId) });
    },
    meta: {
      successMessage: "Gebühren des Rezepts aktualisiert",
    },
  });
};

/**
 * Replaces: softDeleteRechnung from TransactionsStore
 * Used by: PatientRechnungTable delete button
 */
export const useDeletePatientRechnung = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();

  return useMutation<string, Error, { rechnungId: string; patientId: number; rxId?: string }>({
    mutationFn: ({ rechnungId }) => transaction.softDeleteRechnung(rechnungId),

    onSuccess: (_data, { patientId, rxId }) => {
      qc.invalidateQueries({
        queryKey: transactionKeys.patientRechnungen.forPatient(patientId),
      });
      qc.invalidateQueries({
        queryKey: transactionKeys.patientBillsInfo.forPatient(patientId),
      });
      qc.invalidateQueries({
        queryKey: transactionKeys.unpaidBadgeCount.forPatient(patientId),
      });
      if (rxId) {
        qc.invalidateQueries({
          queryKey: transactionKeys.rxBillingTimeline.forRx(rxId),
        });
      }
    },
    meta: {
      successMessage: "Rechnung erfolgreich gelöscht",
      errorMessage: "Fehler beim Löschen der Rechnung",
    },
  });
};

/**
 * Emails a patient's billing documents (Rechnung and/or Mahnungen) as PDF attachments.
 * The email subject, body and template are fixed server-side; the frontend only selects
 * which documents to attach via the SendBillEmailDto.
 * Used by: SendBillEmailDialog (opened from NewPatientRechnungTable).
 */
export const useSendBillEmail = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();

  return useMutation<
    GetRechnungDetailsDto,
    Error,
    { rechnungId: string; patientId: number; rxId?: string; dto: SendBillEmailDto }
  >({
    mutationFn: ({ rechnungId, dto }) => transaction.sendBillEmail(rechnungId, dto),
    onSuccess: (_data, { patientId, rxId }) => {
      qc.invalidateQueries({
        queryKey: transactionKeys.patientRechnungen.forPatient(patientId),
      });
      qc.invalidateQueries({
        queryKey: transactionKeys.patientBillsInfo.forPatient(patientId),
      });
      if (rxId) {
        qc.invalidateQueries({
          queryKey: transactionKeys.rxBillingTimeline.forRx(rxId),
        });
      }
    },
    meta: {
      successMessage: "E-Mail erfolgreich versendet",
      errorMessage: "Fehler beim Versenden der E-Mail",
    },
  });
};

export const useCashCancellation = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();

  return useMutation<
    BaseTransactionDetailsDto,
    Error,
    { transactionId: string; reason: string; patientId: number; rxId?: string }
  >({
    mutationFn: ({ transactionId, reason }) =>
      transaction.cancelCashTransaction(transactionId, { reason }),

    onSuccess: (_data, { patientId, rxId }) => {
      qc.invalidateQueries({
        queryKey: transactionKeys.patientTransactions.forPatient(patientId),
      });
      qc.invalidateQueries({
        queryKey: transactionKeys.patientBillsInfo.forPatient(patientId),
      });
      qc.invalidateQueries({
        queryKey: transactionKeys.patientRechnungen.forPatient(patientId),
      });
      qc.invalidateQueries({
        queryKey: transactionKeys.unpaidBadgeCount.forPatient(patientId),
      });
      qc.invalidateQueries({
        queryKey: taxationKeys.dashboard.all(),
      });
      if (rxId) {
        qc.invalidateQueries({
          queryKey: transactionKeys.rxDuePayment.forRx(rxId),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.rxBillingTimeline.forRx(rxId),
        });
      }
    },
    meta: {
      successMessage: "Stornierung erfolgreich erstellt",
      errorMessage: "Fehler beim Erstellen der Stornierung",
    },
  });
};

export const useECCancellation = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();

  return useMutation<
    BaseTransactionDetailsDto,
    Error,
    { transactionId: string; reason: string; patientId: number; rxId?: string }
  >({
    mutationFn: ({ transactionId, reason }) =>
      transaction.cancelECTransaction(transactionId, { reason }),

    onSuccess: (_data, { patientId, rxId }) => {
      qc.invalidateQueries({
        queryKey: transactionKeys.patientTransactions.forPatient(patientId),
      });
      qc.invalidateQueries({
        queryKey: transactionKeys.patientBillsInfo.forPatient(patientId),
      });
      qc.invalidateQueries({
        queryKey: transactionKeys.patientRechnungen.forPatient(patientId),
      });
      qc.invalidateQueries({
        queryKey: transactionKeys.unpaidBadgeCount.forPatient(patientId),
      });
      if (rxId) {
        qc.invalidateQueries({
          queryKey: transactionKeys.rxDuePayment.forRx(rxId),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.rxBillingTimeline.forRx(rxId),
        });
      }
    },
    meta: {
      successMessage: "Stornierung erfolgreich erstellt",
      errorMessage: "Fehler beim Erstellen der Stornierung",
    },
  });
};

export const useBankTransferCancellation = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();

  return useMutation<
    string,
    Error,
    { bankTransferId: string; reason: string; patientId: number; rxId?: string }
  >({
    mutationFn: ({ bankTransferId, reason }) =>
      transaction.cancelBankTransfer(bankTransferId, { reason }),

    onSuccess: (_data, { patientId, rxId }) => {
      qc.invalidateQueries({
        queryKey: transactionKeys.patientTransactions.forPatient(patientId),
      });
      qc.invalidateQueries({
        queryKey: transactionKeys.patientBillsInfo.forPatient(patientId),
      });
      qc.invalidateQueries({
        queryKey: transactionKeys.patientRechnungen.forPatient(patientId),
      });
      qc.invalidateQueries({
        queryKey: transactionKeys.unpaidBadgeCount.forPatient(patientId),
      });
      if (rxId) {
        qc.invalidateQueries({
          queryKey: transactionKeys.rxDuePayment.forRx(rxId),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.rxRechnungen.forRx(rxId),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.rxBillingTimeline.forRx(rxId),
        });
      }
    },
    meta: {
      successMessage: "Stornierung erfolgreich erstellt",
      errorMessage: "Fehler beim Erstellen der Stornierung",
    },
  });
};

// ===========================
// PATIENT TRANSACTION MUTATIONS
// ===========================

/**
 * Replaces: createTransaction from TransactionsStore
 * Used by: Payment dialogs and transaction forms
 */
export const useCreatePatientTransaction = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreatePatientTransactionDto) => transaction.createTransaction(dto),
    onSuccess: (_data, dto) => {
      const pid = dto.patientId;
      const rxId = dto.rxId;

      if (pid) {
        qc.invalidateQueries({
          queryKey: transactionKeys.patientTransactions.forPatient(pid),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(pid),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.unpaidBadgeCount.forPatient(pid),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.debitPermissions.forPatient(pid),
        });
      }
      if (rxId) {
        qc.invalidateQueries({
          queryKey: transactionKeys.rxDuePayment.forRx(rxId),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.rxBillingTimeline.forRx(rxId),
        });
      }
    },
    meta: {
      successMessage: "Transaktion erfolgreich erstellt",
      errorMessage: "Fehler beim Erstellen der Transaktion",
    },
  });
};

/**
 * Replaces: createServicesTransaction from TransactionsStore
 * Used by: Service billing forms
 */
export const useCreateServicesTransaction = () => {
  const { transaction } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      isGiftCertificate,
      createDto,
    }: {
      isGiftCertificate: boolean;
      createDto: CreateServicesTransactionDto;
    }) => transaction.createServicesTransaction(createDto, isGiftCertificate),
    onSuccess: (data, variables) => {
      // Invalidate patient billing data
      if ("patientId" in variables.createDto && variables.createDto.patientId) {
        queryClient.invalidateQueries({
          queryKey: transactionKeys.patientTransactions.forPatient(variables.createDto.patientId),
        });
        queryClient.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(variables.createDto.patientId),
        });
      }
    },
    meta: {
      successMessage: "Service-Transaktion erfolgreich erstellt",
      errorMessage: "Fehler beim Erstellen der Service-Transaktion",
    },
  });
};

// ===========================
// BANK TRANSFER MUTATIONS
// ===========================

/**
 * Replaces: createBankTransfer from TransactionsStore
 * Used by: Bank transfer forms
 */
export const useCreateBankTransfer = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateBankTransferDto) => transaction.createBankTransferForRechnung(dto),
    onSuccess: (_data, dto) => {
      const pid = dto.patientId;

      if (pid) {
        qc.invalidateQueries({
          queryKey: transactionKeys.patientTransactions.forPatient(pid),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(pid),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.patientRechnungen.forPatient(pid),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.unpaidBadgeCount.forPatient(pid),
        });
      }
    },
  });
};

export const useNoShowInvoiceTransaction = () => {
  const { transaction } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    appointmentId: string;
    rechnungDto: CreateRechnungDto;
    patientId: number;
  };

  return useMutation({
    mutationFn: async (variables: Vars) => {
      const response = await transaction.noShowInvoiceTransaction(
        variables.appointmentId,
        variables.rechnungDto,
      );
      return response;
    },
    onSuccess: (data, variables) => {
      // Invalidate patient billing data - payment affects bill status
      queryClient.invalidateQueries({
        queryKey: transactionKeys.patientBillsInfo.forPatient(variables.patientId),
      });
      queryClient.invalidateQueries({
        queryKey: transactionKeys.patientTransactions.forPatient(variables.patientId),
      });
      queryClient.invalidateQueries({
        queryKey: transactionKeys.patientRechnungen.forPatient(variables.patientId),
      });
    },

    meta: {
      successMessage: "Ausfallrechnung erfolgreich verbucht",
      errorMessage: "Fehler beim Verbuchen der Ausfallrechnung",
    },
  });
};

export const useSoftDeleteDebitPermission = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();
  type Vars = {
    formId: string;
    patientId: number;
  };
  return useMutation({
    mutationFn: ({ formId }: Vars) => transaction.softDeleteDebitPermission(formId),
    onSuccess: (_data, { patientId }) => {
      // Invalidate patient billing data
      if (patientId) {
        qc.invalidateQueries({
          queryKey: transactionKeys.debitPermissions.forPatient(patientId),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(patientId),
        });
      }
    },
    meta: {
      successMessage: "Lastschriftmandat erfolgreich gelöscht",
      errorMessage: "Fehler beim Löschen des Lastschriftmandats",
    },
  });
};

export const useCreateOrUpdateDebitPermission = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();
  type Vars = {
    patientId: number;
    dto: DebitPermissionDto;
  };
  return useMutation({
    mutationFn: async ({ dto, patientId }: Vars) => {
      const response = await transaction.createOrUpdateDebitPermission(patientId, dto);
      return response;
    },
    onSuccess: (_data, variables) => {
      // Invalidate patient billing data
      if (variables.patientId) {
        qc.invalidateQueries({
          queryKey: transactionKeys.debitPermissions.forPatient(variables.patientId),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(variables.patientId),
        });
      }
    },
    meta: {
      successMessage: "Lastschriftmandat erfolgreich gespeichert",
      errorMessage: "Fehler beim Speichern des Lastschriftmandats",
    },
  });
};

export const useGenerateDebitPermissionPdf = () => {
  const { transaction } = useApi();
  type Vars = {
    formId: string;
  };
  return useMutation({
    mutationFn: ({ formId }: Vars) => {
      const response = transaction.generateDebitPermissionPdf(formId);
      return response;
    },

    meta: {
      successMessage: "Lastschriftmandat PDF erfolgreich generiert",
      errorMessage: "Fehler beim Generieren des Lastschriftmandat PDFs",
    },
  });
};

// ===========================
// CREDIT PERMISSION MUTATIONS (for refunds)
// ===========================

/**
 * Creates or updates a credit permission (bank account info for refunds)
 * Similar to useCreateOrUpdateDebitPermission but for SEPA credit transfers
 */
export const useCreateOrUpdateCreditPermission = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();
  type Vars = {
    patientId: number;
    dto: DebitPermissionDto;
  };
  return useMutation({
    mutationFn: async ({ dto, patientId }: Vars) => {
      const response = await transaction.createOrUpdateCreditPermission(dto);
      return response;
    },
    onSuccess: (_data, variables) => {
      // Invalidate patient credit permissions
      if (variables.patientId) {
        qc.invalidateQueries({
          queryKey: transactionKeys.creditPermissions.forPatient(variables.patientId),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(variables.patientId),
        });
      }
    },
    meta: {
      successMessage: "Gutschriftmandat (Credit Permission) erfolgreich gespeichert",
      errorMessage: "Fehler beim Speichern des Gutschriftmandats",
    },
  });
};

/**
 * Creates a refund transaction using credit permission
 * Creates a refund transaction using credit permission
 * Used when patient has overpaid and needs money back via SEPA credit transfer
 */
export const useCreateRefund = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateRefundDto) => transaction.createRefund(dto),
    onSuccess: (_data, dto) => {
      const pid = dto.patientId;
      const rxId = dto.rxId;
      if (pid) {
        qc.invalidateQueries({
          queryKey: transactionKeys.patientTransactions.forPatient(pid),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(pid),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.unpaidBadgeCount.forPatient(pid),
        });
      }
      if (rxId) {
        qc.invalidateQueries({
          queryKey: transactionKeys.rxDuePayment.forRx(rxId),
        });
        qc.invalidateQueries({
          queryKey: transactionKeys.rxBillingTimeline.forRx(rxId),
        });
      }
    },
    meta: {
      successMessage: "Rückerstattung erfolgreich erstellt",
      errorMessage: "Fehler beim Erstellen der Rückerstattung",
    },
  });
};

/**
 * Soft delete credit permission
 * Similar to debit permission deletion but for refund permissions
 */
export const useSoftDeleteCreditPermission = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();
  type Vars = {
    formId: string;
    patientId: number;
  };
  return useMutation({
    mutationFn: ({ formId }: Vars) => transaction.softDeleteDebitPermission(formId), // Reuse same API method
    onSuccess: (_data, { patientId }) => {
      if (patientId) {
        qc.invalidateQueries({
          queryKey: transactionKeys.creditPermissions.forPatient(patientId),
        });
      }
    },
    meta: {
      successMessage: "Gutschriftmandat erfolgreich gelöscht",
      errorMessage: "Fehler beim Löschen des Gutschriftmandats",
    },
  });
};

/**
 * Generate credit permission PDF
 * Similar to debit permission PDF but for refund permissions
 */
export const useGenerateCreditPermissionPdf = () => {
  const { transaction } = useApi();
  type Vars = {
    formId: string;
  };
  return useMutation({
    mutationFn: ({ formId }: Vars) => {
      const response = transaction.generateDebitPermissionPdf(formId); // Reuse same API method
      return response;
    },
    meta: {
      successMessage: "Gutschriftmandat PDF erfolgreich generiert",
      errorMessage: "Fehler beim Generieren des Gutschriftmandat PDFs",
    },
  });
};

// ===========================
// UTILITY FUNCTIONS FOR INVALIDATION
// ===========================

/**
 * Utility function to invalidate all patient billing data for a specific patient
 * we can use it to refresh all patient billing data after major operations
 */
export const invalidatePatientBillingData = (
  queryClient: ReturnType<typeof useQueryClient>,
  patientId: number,
) => {
  queryClient.invalidateQueries({
    queryKey: transactionKeys.patientBillsInfo.forPatient(patientId),
  });
  queryClient.invalidateQueries({
    queryKey: transactionKeys.patientTransactions.forPatient(patientId),
  });
  queryClient.invalidateQueries({
    queryKey: transactionKeys.patientRechnungen.forPatient(patientId),
  });
  queryClient.invalidateQueries({
    queryKey: transactionKeys.debitPermissions.forPatient(patientId),
  });
};

/**
 * Utility function to invalidate all billing data
 */
export const invalidateAllBillingData = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({
    queryKey: transactionKeys.patientBillsInfo.all(),
  });
  queryClient.invalidateQueries({
    queryKey: transactionKeys.patientTransactions.all(),
  });
  queryClient.invalidateQueries({
    queryKey: transactionKeys.patientRechnungen.all(),
  });
  queryClient.invalidateQueries({
    queryKey: transactionKeys.rxRechnungen.all(),
  });
};

// ===========================
// EXPORT MODULE MUTATIONS
// ===========================

export const useUpdateExportedStatus = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      transactionId,
      updateData,
    }: {
      transactionId: string;
      updateData: UpdateExportStatusDto;
    }) => transaction.updateExportAndBillingStatus(transactionId, updateData),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: transactionKeys.insuranceExport.forTransactionId(variables.transactionId),
      });
    },
    meta: {
      successMessage: "Der Transaktionsstatus wurde erfolgreich aktualisiert.",
      errorMessage: "Fehler beim Aktualisieren des Exportstatus",
    },
  });
};

export const useRemoveRxFromTransaction = () => {
  const { transaction } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      transactionId,
      rxId,
      reason,
      locationId,
    }: {
      transactionId: string;
      rxId: string;
      reason: string;
      locationId: string | undefined;
    }) =>
      transaction.removeRxFromTransaction(transactionId, rxId, locationId, {
        reason,
      }),

    onSuccess: (_data, variables) => {
      // Invalidate the export details to refresh the modal
      qc.invalidateQueries({
        queryKey: transactionKeys.insuranceExport.forTransactionId(variables.transactionId),
      });
      qc.invalidateQueries({
        queryKey: transactionKeys.transactionTickets.forTransaction(variables.transactionId),
      });
    },

    meta: {
      successMessage: "Rx erfolgreich aus der Transaktion entfernt",
      errorMessage: "Fehler beim Entfernen des Rx",
    },
  });
};

// ===========================
// DELAY STATUS MUTATIONS
// ===========================

export interface UpdateDelayStatusDto {
  isDelayed: boolean;
  amountDelayed?: number;
}

export const useUpdateDelayStatus = () => {
  const qc = useQueryClient();
  const baseUrl = process.env.REACT_APP_API_URL;

  return useMutation({
    mutationFn: async ({
      transactionId,
      updateData,
    }: {
      transactionId: string;
      updateData: UpdateDelayStatusDto;
    }) => {
      const token = getRegisteredAuthStore()?.authToken || "";
      const res = await axios.patch(
        `${baseUrl}/transactions/insurance/bill/delay-status/${transactionId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return res.data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: transactionKeys.insuranceExport.forTransactionId(variables.transactionId),
      });
    },
    meta: {
      successMessage: "Verzögerungsstatus erfolgreich aktualisiert.",
      errorMessage: "Fehler beim Aktualisieren des Verzögerungsstatus",
    },
  });
};
